import axios from "axios";
import { req } from "./req";
import type { KscResponseModel } from "../../../common/core/request";
import type {
  AddNodesParam,
  AddNodesResponse,
  CordonNodeParam,
  CordonNodeResponse,
  DeleteNodeParam,
  DeleteNodeResponse,
  DescribeNodesParam,
  DescribeNodesResponse,
  DrainNodeParam,
  DrainNodeResponse,
  ExportNodesParam,
  ModifyNodeComponentsParam,
  ModifyNodeComponentsResponse,
  ModifyNodeDeleteProtectParams,
  ModifyNodeDeleteProtectResponse,
  RemoveNodeLabelsParam,
  RemoveNodeLabelsResponse,
  RemoveNodeTaintsParam,
  RemoveNodeTaintsResponse,
  SetNodeLabelsParam,
  SetNodeLabelsResponse,
  SetNodeTaintsParam,
  SetNodeTaintsResponse,
  UnCordonNodeParam,
  UnCordonNodeResponse,
  UnDrainNodeParam,
  UnDrainNodeResponse,
} from "./types";
import type { Param, WithRegion } from "../../../common/api/types";
import { AxiosRESTDataSource } from "@kce2/common/api/v2/rest-data-source";
import {
  GetStandardImagesByProductResponseSchema,
  type GetStandardImagesByProductRequest,
  CheckInstallingMasterComponentResponseSchema,
  type CheckInstallingMasterComponentRequest,
  DescribeAddNodeScriptsResponseSchema,
  type DescribeAddNodeScriptsRequest,
  type ReinstallNodesRequest,
  ReinstallNodesResponseSchema,
  type CheckEpcReinstallConditionRequest,
  CheckEpcReinstallConditionResponseSchema,
} from "./schema/node";
import { axiosInstance } from "@kce2/app/axios";

export async function describeNodes(params: DescribeNodesParam) {
  return axios<KscResponseModel<DescribeNodesResponse>>(
    req.post("DescribeNodes", params.Region, {
      body: params,
    }),
  ).then(v => v.data || {});
}

export async function addNodes(params: AddNodesParam) {
  return axios<KscResponseModel<AddNodesResponse>>(
    req.post("AddNodes", params.Region, {
      data: params,
    }),
  );
}

export async function deleteNode(params: DeleteNodeParam) {
  return axios<DeleteNodeResponse>(
    req.post("DeleteNode", params.Region, {
      data: params,
    }),
  );
}

export async function drainNode(params: DrainNodeParam) {
  return axios<DrainNodeResponse>(
    req.get("DrainNode", params.Region, {
      params,
    }),
  );
}

export async function unDrainNode(params: UnDrainNodeParam) {
  return axios<UnDrainNodeResponse>(req.get("UnDrainNode", params.Region, { params }));
}

export async function cordonNode(params: CordonNodeParam) {
  return axios<CordonNodeResponse>(req.get("CorDonNode", params.Region, { params }));
}

export async function unCordonNode(params: UnCordonNodeParam) {
  return axios<UnCordonNodeResponse>(req.get("UnCorDonNode", params.Region, { params }));
}

export async function setNodeLabels(params: SetNodeLabelsParam) {
  return axios<SetNodeLabelsResponse>(req.post("AddNodeLabels", params.Region, { data: params }));
}

export async function removeNodeLabels(params: RemoveNodeLabelsParam) {
  return axios<RemoveNodeLabelsResponse>(
    req.post("DeleteNodeLabels", params.Region, { data: params }),
  );
}

export async function setNodeTaints(params: SetNodeTaintsParam) {
  return axios<SetNodeTaintsResponse>(req.post("AddNodeTaints", params.Region, { data: params }));
}

export async function removeNodeTaints(params: RemoveNodeTaintsParam) {
  return axios<RemoveNodeTaintsResponse>(
    req.post("DeleteNodeTaints", params.Region, { data: params }),
  );
}

export async function modifyNodeComponents(params: ModifyNodeComponentsParam) {
  return axios<ModifyNodeComponentsResponse>(
    req.post("ModifyNodeComponents", params.Region, {
      body: {
        ClusterId: params.ClusterId,
        MachineId: params.MachineId,
        Components: params.Components,
      },
    }),
  );
}

export async function modifyNodeDeleteProtect(params: WithRegion<ModifyNodeDeleteProtectParams>) {
  return axios<ModifyNodeDeleteProtectResponse>(
    req.post("ModifyNodeDeleteProtect", params.Region, {
      data: params,
    }),
  );
}

export async function exportNodes(params: WithRegion<ExportNodesParam>) {
  return axios<Blob>(
    req.post("ExportNodes", params.Region, {
      params: {
        ClusterId: params.ClusterId,
        ClusterName: params.ClusterName,
        NodeIp: params.NodeIp,
        Page: params.Page,
        PageSize: params.PageSize,
        IsExportAll: params.IsExportAll,
      },
      data: {
        ExportNames: params.ExportNames,
        AvailabilityZones: params.AvailabilityZones,
        NodeStatus: params.NodeStatus,
        Role: params.Role,
        Labels: params.Labels,
        InstanceIds: params.InstanceIds,
      },
      headers: {
        "Content-Type": "application/force-download",
        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      responseType: "blob",
    }),
  );
}

export class NodeAPI extends AxiosRESTDataSource {
  baseURL = "/api/cluster";

  async checkInstallingMasterComponent({
    Region,
    ...params
  }: Param<CheckInstallingMasterComponentRequest>) {
    return this.get("", CheckInstallingMasterComponentResponseSchema, {
      Action: "CheckInstallingMasterComponent",
      Region,
      params,
    });
  }

  async getStandardImagesByProduct({
    Region,
    ProductTypeId,
  }: Param<GetStandardImagesByProductRequest> & { ProductTypeId: string }) {
    return this.get("", GetStandardImagesByProductResponseSchema, {
      Action: "GetStandardImagesByProduct",
      Region,
      params: { ProductTypeId },
    });
  }

  async describeAddNodeScripts({ Region }: Param<DescribeAddNodeScriptsRequest>) {
    return this.get("", DescribeAddNodeScriptsResponseSchema, {
      Action: "DescribeAddNodeScripts",
      Region,
      params: {},
    });
  }

  /**
   * 重装系统（节点管理页对已纳管节点重装）。
   * POST ReinstallNodes（Version 2023-01-01）。关机+重装由后端单接口原子封装，
   * 前端只调一次。批量+单节点同接口（InstanceIds 数组，单节点传 [id]）。
   */
  async reinstallNodes({ Region, ...params }: Param<ReinstallNodesRequest>) {
    return this.post("", ReinstallNodesResponseSchema, {
      Action: "ReinstallNodes",
      Region,
      body: params,
    });
  }

  /**
   * 查询 EPC 裸金属节点是否可重装（底层状态检查）。
   * POST CheckEpcReinstallCondition（Version 2023-01-01）。
   * 返回每节点 Reinstallable（纯 boolean，无原因字段）。KEC 不调此接口。
   */
  async checkEpcReinstallCondition({
    Region,
    ...params
  }: Param<CheckEpcReinstallConditionRequest>) {
    return this.post("", CheckEpcReinstallConditionResponseSchema, {
      Action: "CheckEpcReinstallCondition",
      Region,
      body: params,
    });
  }
}

export const nodeApi = new NodeAPI(axiosInstance);
